import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../../auth/service/auth.service';
import { AlertsService } from '@app/shared/alerts';
import { Router } from '@angular/router';
import { CleanupService } from '@app/store/services/cleanup.service';
import { CredentailsModel } from '@app/account/models';
import { TokenService } from '@app/shared';

/**
 *  Call refrest token request on 401 error
 */
@Injectable({
    providedIn: 'root',
})
export class ApiTokenRefreshInterceptor implements HttpInterceptor {
    private inflightAuthRequest: null | Observable<HttpResponse<CredentailsModel>>;

    constructor(
        private injector: Injector,
        private alertsService: AlertsService,
        private router: Router,
        private cleanupService: CleanupService,
        private tokenService: TokenService,
    ) {
        this.inflightAuthRequest = null;
    }

    /**
     * Get Inflight Auth
     */
    private get inflightAuth(): Observable<HttpResponse<CredentailsModel>> {
        const authService = this.injector.get(AuthService);
        if (!this.inflightAuthRequest) {
            this.inflightAuthRequest = authService.refresh();
        }
        return this.inflightAuthRequest;
    }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                    return this.refreshTokenRequest(next, request);
                }
                return throwError(() => error);
            }),
        );
    }

    /**
     * Token Refresh process
     */
    private refreshTokenRequest(next: HttpHandler, authReq: HttpRequest<unknown>) {
        return this.inflightAuth.pipe(
            switchMap(() => {
                this.clearInflightAuth();
                return next.handle(
                    authReq.clone({
                        setHeaders: {
                            Authorization: `Bearer ${this.tokenService.getToken()}`,
                        },
                    }),
                );
            }),
            catchError((error: HttpErrorResponse) => {
                // show message
                if (error.status === 401) {
                    this.alertsService.show('auth.token.expired', 'danger');
                    this.cleanupService.cleanup();
                    void this.router.navigate(['/']);
                }
                // remove inflight auth request
                this.clearInflightAuth();
                return throwError(() => error);
            }),
        );
    }

    /**
     * Clear inflightAuthRequest
     */
    private clearInflightAuth() {
        this.inflightAuthRequest = null;
    }
}
